import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../../common/types';
import { catalogLinksMessages as m } from './catalogLinksMessages';
import { CatalogLinksServiceRow, presaveChipOf } from './CatalogLinksServiceRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './CatalogLinks.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The services with no pre-save: they show from release day (design c1). */
const PLAY_ONLY_SERVICES: readonly string[] = [MusicAPIIntegrationID.YoutubeMusic, MusicAPIIntegrationID.SoundCloud];

interface CatalogLinksPresaveServicesProps {
  /** The services that are on, in page order. */
  services: readonly string[];
  disabled: boolean;
  onChange: (services: string[]) => void;
}

/** "Services, in page order" before the release (design c1): the pre-save services with a switch each. */
const CatalogLinksPresaveServicesComponent: React.FC<CatalogLinksPresaveServicesProps> = ({
  services,
  disabled,
  onChange,
}) => {
  const intl = useIntl();
  const rows = useMemo(
    () => [...services, ...defaultPresaveServices.filter((service) => !services.includes(service))],
    [services]
  );
  const onToggle = useCallback(
    (service: string, on: boolean) => {
      const next = on ? [...services, service] : services.filter((one) => one !== service);
      // Keep the page order of the list.
      onChange(rows.filter((one) => next.includes(one)));
    },
    [onChange, rows, services]
  );

  return (
    <div className={styles.field}>
      <span className={styles.label}>
        {intl.formatMessage(m.services)}
        <span className={styles.labelEnd}>
          {intl.formatMessage(m.servicesCount, {
            on: services.length,
            all: rows.length + PLAY_ONLY_SERVICES.length,
          })}
        </span>
      </span>
      <div className={styles.list}>
        {rows.map((service) => (
          <CatalogLinksServiceRow
            key={service}
            service={service}
            sub={null}
            chip={intl.formatMessage(presaveChipOf(service) === 'preAdd' ? m.preAddLabel : m.presaveLabel)}
            isOn={services.includes(service)}
            disabled={disabled}
            onToggle={onToggle}
          />
        ))}
        {PLAY_ONLY_SERVICES.map((service) => (
          <CatalogLinksServiceRow key={service} service={service} sub={intl.formatMessage(m.playOnly)} chip={null} />
        ))}
      </div>
      <p className={styles.help}>{intl.formatMessage(m.servicesHelp)}</p>
    </div>
  );
};

export const CatalogLinksPresaveServices = TypedMemo(CatalogLinksPresaveServicesComponent);
